import dayjs from "dayjs";
import { isImageContentType } from "../../../../common/utils/getFileTypeIcon";
import type {
  FileRelationsDetailed,
  TicketItemDetailed,
  TicketRetrieve,
} from "../../../generated-axios-client";

// Only these can be placed in the PDF; other images (e.g. HEIC) are shown disabled.
const REPORT_IMAGE_CONTENT_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

export type ReportPicture = {
  fileId: string;
  name: string;
  caption: string;
  contentType: string;
  canBeIncluded: boolean;
};

export type ReportPictureSelection = Record<string, string[]>;

// Selections are keyed by task id; pictures not linked to a task use this key.
export const GENERAL_PICTURES_KEY = "general";

function toReportPicture(relation: FileRelationsDetailed): ReportPicture | null {
  const file = relation.file;
  if (!file?.id || !isImageContentType(file.content_type)) return null;
  return {
    fileId: file.id,
    name: file.name ?? "",
    caption: relation.description || file.name || "",
    contentType: file.content_type,
    canBeIncluded: REPORT_IMAGE_CONTENT_TYPES.has(file.content_type),
  };
}

/** Pictures of each task, plus the work order's own pictures under GENERAL_PICTURES_KEY. */
export function groupReportPictures(
  ticket: TicketRetrieve
): Record<string, ReportPicture[]> {
  const pictures: Record<string, ReportPicture[]> = {};
  const seenFileIds = new Set<string>();
  for (const relation of ticket.file_relations ?? []) {
    const picture = toReportPicture(relation);
    if (!picture || seenFileIds.has(picture.fileId)) continue;
    seenFileIds.add(picture.fileId);
    const key = relation.ticket_item ?? GENERAL_PICTURES_KEY;
    pictures[key] = [...(pictures[key] ?? []), picture];
  }
  return pictures;
}

// The PDF's order: by task number, older tasks without one last, then by creation.
export function sortTasksForReport(tasks: TicketItemDetailed[]): TicketItemDetailed[] {
  return [...tasks].sort((first, second) => {
    const firstNumber = first.ticket_item_number ?? Number.POSITIVE_INFINITY;
    const secondNumber = second.ticket_item_number ?? Number.POSITIVE_INFINITY;
    if (firstNumber !== secondNumber) return firstNumber < secondNumber ? -1 : 1;
    return (first.created_at ?? "").localeCompare(second.created_at ?? "");
  });
}

// Mirrors the server's file name, so the summary shows the name the download gets.
export function reportFilename(ticket: TicketRetrieve): string {
  const parts = [`WO-${ticket.ticket_number}`, dayjs().format("YYYY-MM-DD")];
  const servicePart = (ticket.service?.name ?? "")
    .replace(/[^\p{L}\p{N}_]+/gu, "-")
    .replace(/^-+|-+$/g, "");
  if (servicePart) parts.push(servicePart);
  return `${parts.join("_")}.pdf`;
}
